import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "../context/GameContext";
import { PETS, PET_ACCESSORIES } from "../lib/content";
import { ASSETS, PET_IMAGES, ACC_IMAGES } from "../lib/assets";
import TopBar from "../components/TopBar";
import Confetti from "../components/Confetti";
import SquishyButton from "../components/SquishyButton";
import { speak } from "../lib/speech";
import { sound } from "../lib/sounds";
import { Utensils, Gamepad2, Moon, Sparkles, Pencil, Coins, Check } from "lucide-react";

// Where each accessory sits on the pet (percentages relative to the pet image).
const ACC_LAYOUT = {
  "🎩": { top: "-4%", w: 48 },
  "🧢": { top: "0%", w: 50 },
  "👑": { top: "-2%", w: 46 },
  "🎓": { top: "-4%", w: 52 },
  "👓": { top: "27%", w: 44 },
  "🕶️": { top: "27%", w: 44 },
  "🎀": { top: "4%", left: "70%", w: 24, rot: 12 },
  "🌸": { top: "4%", left: "72%", w: 22, rot: 8 },
  "⭐": { top: "0%", left: "72%", w: 26 },
  "🧣": { top: "52%", w: 54 },
};

const STAGES = {
  baby: { label: "Baby", scale: 0.62 },
  jong: { label: "Jong", scale: 0.82 },
  volwassen: { label: "Volwassen", scale: 1 },
};

const XP_PER_LEVEL = 50;
const MAX_LEVEL = 10;

const WornAccessory = ({ acc }) => {
  const l = ACC_LAYOUT[acc] || { top: "-2%", w: 48 };
  const src = ACC_IMAGES[acc];
  if (!src) return null;
  return (
    <motion.img
      src={src} alt=""
      className="pointer-events-none absolute select-none"
      style={{
        top: l.top, left: l.left || "50%",
        width: `${l.w}%`,
        filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.22))", zIndex: 20,
      }}
      initial={{ x: "-50%", rotate: l.rot || 0 }}
      animate={{ x: "-50%", rotate: [l.rot || 0, (l.rot || 0) - 2, l.rot || 0], y: [0, -2, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
    />
  );
};

const Bar = ({ label, value, color, icon }) => (
  <div className="w-full">
    <div className="flex justify-between font-display text-xs font-bold text-slate-500">
      <span className="flex items-center gap-1">{icon}{label}</span><span>{value}%</span>
    </div>
    <div className="h-3 overflow-hidden rounded-full bg-slate-100">
      <motion.div className="h-full rounded-full" style={{ background: color }} animate={{ width: `${value}%` }} />
    </div>
  </div>
);

export default function Pet() {
  const { profile, updatePet, petCare, addReward } = useGame();
  const [confetti, setConfetti] = useState(false);
  const [chooser, setChooser] = useState(false);
  const [chosenKind, setChosenKind] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const [renaming, setRenaming] = useState(false);
  const pet = profile?.pet || {};

  useEffect(() => {
    if (!profile) return;
    const t = setTimeout(() => speak(pet.hatched
      ? `Dit is ${pet.name}, jouw huisdier!`
      : "Voer je ei op met munten om het te laten uitkomen!"), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  if (!profile) return null;
  const canAfford = (n) => profile.coins >= n;
  const stage = STAGES[pet.stage] || STAGES.baby;
  const level = pet.level || 1;
  const xpInto = (pet.xp || 0) % XP_PER_LEVEL;
  const xpPct = level >= MAX_LEVEL ? 100 : Math.round((xpInto / XP_PER_LEVEL) * 100);
  const owned = pet.hats || [];

  const feedEgg = async () => {
    if (!canAfford(10)) { sound.wrong(); speak("Je hebt niet genoeg munten. Speel meer lessen!"); return; }
    sound.coin();
    await addReward({ coins: -10 });
    const growth = Math.min(100, (pet.growth || 0) + 25);
    if (growth >= 100) {
      await updatePet({ growth: 100 });
      sound.reward();
      speak("Hoera! Je ei komt uit! Kies snel welk huisdier je wilt!");
      setChooser(true);
    } else {
      await updatePet({ growth });
      speak("Bijna! Voer nog een keer.");
    }
  };

  const confirmHatch = async () => {
    const meta = PETS.find((p) => p.kind === chosenKind);
    if (!meta) { speak("Kies eerst een huisdier!"); return; }
    const name = (nameInput.trim() || meta.name);
    setConfetti(true); sound.reward();
    speak(`Hoera! ${name} is geboren! Zorg goed voor ${name}!`);
    await updatePet({
      kind: meta.kind, name, hatched: true, stage: "baby", level: 1, xp: 0,
      growth: 100, happiness: 80, hunger: 80, energy: 80,
    });
    setChooser(false); setChosenKind(null); setNameInput("");
    setTimeout(() => setConfetti(false), 2500);
  };

  const doCare = async (action, msg) => {
    if (action === "feed" && !canAfford(5)) { sound.wrong(); speak("Niet genoeg munten om te voeren!"); return; }
    if (action === "play" && (pet.energy || 0) < 15) { sound.wrong(); speak(`${pet.name} is te moe om te spelen. Laat hem eerst slapen!`); return; }
    try {
      const before = pet.level || 1;
      sound.correct();
      const data = await petCare(action);
      speak(msg);
      if (data?.pet?.level > before) {
        setConfetti(true); sound.reward();
        speak(`${data.pet.name} is gegroeid naar level ${data.pet.level}!`);
        setTimeout(() => setConfetti(false), 2200);
      }
    } catch (e) {
      sound.wrong(); speak("Dat lukte even niet.");
    }
  };

  const buyOrEquip = async (acc) => {
    if (owned.includes(acc.id)) {
      const next = pet.accessory === acc.id ? null : acc.id;
      sound.correct();
      await updatePet({ accessory: next });
      speak(next ? "Wat mooi!" : "Ook goed zo!");
      return;
    }
    if (!canAfford(acc.price)) { sound.wrong(); speak("Niet genoeg munten voor deze!"); return; }
    sound.coin();
    await addReward({ coins: -acc.price });
    await updatePet({ hats: [...owned, acc.id], accessory: acc.id });
    speak("Gekocht! Wat mooi!");
  };

  const saveName = async () => {
    const n = nameInput.trim();
    if (!n) { setRenaming(false); return; }
    await updatePet({ name: n });
    speak(`Je huisdier heet nu ${n}!`);
    setRenaming(false);
  };

  const base = `${(13 * stage.scale).toFixed(1)}rem`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-100 via-sky-50 to-emerald-50">
      <Confetti show={confetti} count={60} />
      <TopBar title="Mijn Huisdier" />
      <div className="mx-auto max-w-md px-4 pb-16 pt-4 flex flex-col items-center gap-6">

        <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 3, repeat: Infinity }}
          className="relative flex h-56 w-56 items-center justify-center">
          {pet.hatched ? (
            <div className="relative" style={{ width: base, height: base }}>
              <img src={PET_IMAGES[pet.kind] || ASSETS.petFox} alt={pet.name} className="h-full w-full object-contain drop-shadow-xl" />
              {pet.accessory && <WornAccessory acc={pet.accessory} />}
            </div>
          ) : (
            <img src={ASSETS.petEgg} alt="Ei" className="h-48 w-48 object-contain drop-shadow-xl" />
          )}
        </motion.div>

        <div className="flex items-center gap-2">
          <h1 data-testid="pet-name" className="font-display text-3xl font-bold text-slate-800">{pet.hatched ? pet.name : "Jouw ei"}</h1>
          {pet.hatched && (
            <button data-testid="pet-rename-btn" onClick={() => { setNameInput(pet.name); setRenaming(true); }}
              className="rounded-full bg-white p-2 text-slate-400 shadow"><Pencil className="h-4 w-4" /></button>
          )}
        </div>

        {pet.hatched && (
          <div className="flex items-center gap-2 font-display text-sm font-bold text-slate-500">
            <span data-testid="pet-stage" className="rounded-full bg-white px-3 py-1 shadow">{stage.label}</span>
            <span data-testid="pet-level" className="rounded-full bg-white px-3 py-1 shadow">Level {level}</span>
          </div>
        )}

        <div className="w-full space-y-3 rounded-3xl bg-white p-5 shadow-lg">
          {pet.hatched ? (
            <>
              {level < MAX_LEVEL ? (
                <div>
                  <div className="flex justify-between font-display text-xs font-bold text-slate-500"><span>Ervaring</span><span>Level {level}</span></div>
                  <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                    <motion.div data-testid="pet-xp-bar" className="h-full rounded-full bg-violet-400" animate={{ width: `${xpPct}%` }} />
                  </div>
                </div>
              ) : (
                <p className="text-center font-display text-sm font-bold text-violet-500">Max level bereikt! 🌟</p>
              )}
              <Bar label="Blijheid" value={pet.happiness || 0} color="#F472B6" icon={<span>😊</span>} />
              <Bar label="Honger" value={pet.hunger || 0} color="#FB923C" icon={<span>🍖</span>} />
              <Bar label="Energie" value={pet.energy || 0} color="#38BDF8" icon={<span>⚡</span>} />
              <div className="mt-3 grid grid-cols-3 gap-2">
                <SquishyButton data-testid="pet-feed-btn" onClick={() => doCare("feed", "Mmm, lekker!")} color="#FB923C" edge="#EA580C" className="flex flex-col items-center justify-center gap-1 py-2 text-sm">
                  <Utensils className="h-5 w-5" /> Voeren
                  <span className="flex items-center gap-0.5 text-[10px]"><Coins className="h-3 w-3" />5</span>
                </SquishyButton>
                <SquishyButton data-testid="pet-play-btn" onClick={() => doCare("play", "Jippie, spelen!")} color="#F472B6" edge="#DB2777" className="flex flex-col items-center justify-center gap-1 py-2 text-sm">
                  <Gamepad2 className="h-5 w-5" /> Spelen
                </SquishyButton>
                <SquishyButton data-testid="pet-sleep-btn" onClick={() => doCare("sleep", "Welterusten, slaap zacht!")} color="#818CF8" edge="#4F46E5" className="flex flex-col items-center justify-center gap-1 py-2 text-sm">
                  <Moon className="h-5 w-5" /> Slapen
                </SquishyButton>
              </div>
            </>
          ) : (
            <>
              <Bar label="Groei" value={pet.growth || 0} color="#4ADE80" icon={<span>🥚</span>} />
              <SquishyButton data-testid="pet-hatch-btn" onClick={feedEgg} color="#4ADE80" edge="#16A34A" className="w-full flex items-center justify-center gap-2 text-lg">
                <Sparkles className="h-5 w-5" /> Voer het ei (🪙 10)
              </SquishyButton>
            </>
          )}
        </div>

        {pet.hatched && (
          <div className="w-full rounded-3xl bg-white p-5 shadow-lg">
            <h2 className="mb-3 font-display text-xl font-bold text-slate-700">Kledingwinkel 🎩</h2>
            <div className="grid grid-cols-4 gap-2">
              {PET_ACCESSORIES.map((h) => {
                const isOwned = owned.includes(h.id);
                const isWorn = pet.accessory === h.id;
                return (
                  <motion.button key={h.id} data-testid={`accessory-${h.id}`} whileTap={{ scale: 0.9 }} onClick={() => buyOrEquip(h)}
                    className={`relative flex flex-col items-center rounded-2xl border-2 p-2 ${isWorn ? "border-emerald-400 bg-emerald-50" : isOwned ? "border-sky-200 bg-sky-50" : "border-slate-100 bg-slate-50"}`}>
                    {isWorn && <span className="absolute -right-1 -top-1 rounded-full bg-emerald-400 p-0.5"><Check className="h-3 w-3 text-white" /></span>}
                    <img src={ACC_IMAGES[h.id]} alt={h.name} className="h-9 w-9 object-contain" />
                    {isOwned
                      ? <span className="text-[9px] font-bold text-sky-600">{isWorn ? "aan" : "bezit"}</span>
                      : <span className="flex items-center gap-0.5 text-[10px] font-bold text-yellow-600"><Coins className="h-2.5 w-2.5" />{h.price}</span>}
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Pet chooser modal */}
      <AnimatePresence>
        {chooser && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div data-testid="pet-chooser" initial={{ scale: 0.8, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }}
              className="w-full max-w-md rounded-[2rem] bg-white p-6 shadow-2xl">
              <h2 className="text-center font-display text-2xl font-bold text-slate-800">Kies jouw huisdier! 🎉</h2>
              <p className="mb-4 text-center font-display text-sm text-slate-500">Tik op je favoriete dier.</p>
              <div className="grid grid-cols-4 gap-2">
                {PETS.map((p) => (
                  <motion.button key={p.kind} data-testid={`choose-${p.kind}`} whileTap={{ scale: 0.9 }}
                    onClick={() => { setChosenKind(p.kind); sound.correct(); speak(p.name); }}
                    className={`flex flex-col items-center rounded-2xl border-2 p-2 ${chosenKind === p.kind ? "border-emerald-400 bg-emerald-50" : "border-slate-100 bg-slate-50"}`}>
                    <img src={PET_IMAGES[p.kind]} alt={p.name} className="h-12 w-12 object-contain" />
                    <span className="text-[10px] font-bold text-slate-500">{p.name}</span>
                  </motion.button>
                ))}
              </div>
              <input data-testid="pet-name-input" value={nameInput} onChange={(e) => setNameInput(e.target.value)} maxLength={16}
                placeholder="Geef een naam..." className="mt-4 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 font-display font-bold text-slate-700 outline-none focus:border-emerald-400" />
              <SquishyButton data-testid="pet-confirm-btn" onClick={confirmHatch} color="#4ADE80" edge="#16A34A" className="mt-4 w-full text-lg" disabled={!chosenKind}>
                Laat uitkomen! 🐣
              </SquishyButton>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rename modal */}
      <AnimatePresence>
        {renaming && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8, opacity: 0 }}
              className="w-full max-w-sm rounded-[2rem] bg-white p-6 shadow-2xl">
              <h2 className="mb-3 text-center font-display text-xl font-bold text-slate-800">Nieuwe naam</h2>
              <input data-testid="rename-input" value={nameInput} onChange={(e) => setNameInput(e.target.value)} maxLength={16}
                className="w-full rounded-2xl border-2 border-slate-200 px-4 py-3 font-display font-bold text-slate-700 outline-none focus:border-emerald-400" />
              <div className="mt-4 flex gap-3">
                <SquishyButton onClick={() => setRenaming(false)} color="#FFFFFF" edge="#CBD5E1" textClass="text-slate-600" className="flex-1">Annuleren</SquishyButton>
                <SquishyButton data-testid="rename-save-btn" onClick={saveName} color="#4ADE80" edge="#16A34A" className="flex-1">Opslaan</SquishyButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
